import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sprout, Milk, GraduationCap, Handshake } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { PRODUCTS, SERVICES } from "@/lib/content";

const iconMap = { sprout: Sprout, milk: Milk, "graduation-cap": GraduationCap, handshake: Handshake };

export default function ProductsPage() {
  return (
    <div data-testid="products-page">
      <section className="bg-primary text-white py-20 md:py-24 relative overflow-hidden">
        <div className="absolute inset-0 opacity-15">
          <img
            src="https://images.pexels.com/photos/29465456/pexels-photo-29465456.jpeg?auto=compress&cs=tinysrgb&w=2000"
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="text-xs uppercase tracking-widest text-accent font-semibold">Products & Services</span>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mt-3 mb-4" style={{ fontFamily: "Outfit" }}>
            Quality for better productivity
          </h1>
          <p className="text-white/80 max-w-2xl mx-auto">
            From cattle feed to agricultural inputs, we provide everything farmers need for successful and profitable
            farming.
          </p>
        </div>
      </section>

      {/* Products grid */}
      <section className="py-20 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 max-w-2xl mx-auto">
            <span className="text-xs uppercase tracking-widest text-primary font-semibold">Our Products</span>
            <h2 className="text-3xl md:text-4xl font-bold mt-2" style={{ fontFamily: "Outfit" }}>
              Trusted by farmers across the region
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {PRODUCTS.map((p) => (
              <Card key={p.key} className="overflow-hidden rounded-2xl border-border group hover:shadow-lg transition-shadow" data-testid={`product-card-${p.key}`}>
                <div className="aspect-square bg-secondary/40 overflow-hidden">
                  <img src={p.image} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-6">
                  <div className="text-xs uppercase tracking-widest text-primary font-semibold mb-1.5">{p.tagline}</div>
                  <h3 className="text-lg font-semibold mb-2" style={{ fontFamily: "Outfit" }}>{p.name}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed mb-4">{p.description}</p>
                  <Button asChild variant="outline" size="sm" className="rounded-full border-primary text-primary hover:bg-secondary w-full" data-testid={`product-enquire-${p.key}`}>
                    <Link to="/contact?form=enquiry">Enquire Now</Link>
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-20 md:py-24 bg-secondary/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 max-w-2xl mx-auto">
            <span className="text-xs uppercase tracking-widest text-primary font-semibold">Our Services</span>
            <h2 className="text-3xl md:text-4xl font-bold mt-2" style={{ fontFamily: "Outfit" }}>
              End-to-end farmer support
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {SERVICES.map((s) => {
              const Icon = iconMap[s.icon];
              return (
                <Card key={s.key} className="p-7 rounded-2xl border-border bg-white hover:-translate-y-1 hover:shadow-lg transition-all" data-testid={`service-detail-${s.key}`}>
                  <div className="w-14 h-14 rounded-2xl bg-secondary text-primary flex items-center justify-center mb-5">
                    <Icon className="w-7 h-7" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2" style={{ fontFamily: "Outfit" }}>{s.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{s.description}</p>
                </Card>
              );
            })}
          </div>

          <div className="text-center mt-12">
            <Button asChild className="rounded-full bg-primary hover:bg-primary/90" data-testid="services-enquire-btn">
              <Link to="/contact?form=enquiry">
                Get In Touch <ArrowRight className="w-4 h-4 ml-1.5" />
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
